/**
 * 科研仪器新版配置
 * 图片统一走 instruments_new/aliases 短英文名
 *
 * 切图映射（aliases）:
 * banner-bg ← banner/chuangxinyangling-keyanyiji
 * icon-type ← enterprise icon-type
 * icon-unit ← home_new icon-unit
 * icon-search ← enterprise icon-search
 * cover-fallback ← chuangxinyangling/keyanyiqi/yiqi
 */
import { deepCloneConfig } from '@/utils/deepCloneConfig'
import { applyPortalZhToRu } from '@/utils/applyPortalZhToRu'
import { getCurrentLocale } from '@/utils/i18nText'
import { L, resolveLocaleImages } from '@/utils/localeAsset'

const A = (name) => L(`instruments_new/aliases/${name}`)
const Nav = (name) => L(`home_new/aliases/${name}`)

const zhCN = {
  siteInfo: {
    title: '杨凌农业科技成果转化综合信息服务平台',
    logo: L('shouye/web-logo.png'),
    loginText: '登录',
    registerText: '注册',
    logoutText: '退出'
  },
  breadcrumb: {
    home: '首页',
    parent: '科创服务',
    parentPath: '/innovation-service',
    current: '科研仪器'
  },
  header: {
    titleMain1: '科研',
    titleMain2: '仪器',
    titleSub: '仪器互通，科创共赢',
    searchPlaceholder: '请输入关键词进行搜索',
    searchButton: '搜索',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: '首页', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: '走进杨凌', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: '供需中心', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: '价值评估', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: '技术经理人', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: '交易中心', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: '科创服务', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: '科技人才', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: '农业企业', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  icons: {
    type: A('icon-type.png'),
    unit: A('icon-unit.png'),
    search: A('icon-search.png')
  },
  typeFilter: {
    label: '类型',
    allLabel: '全部'
  },
  card: {
    priceLabel: '服务价格：',
    priceDefault: '面议',
    emptyTip: '待接口提供',
    coverFallback: L('default.png'),
    unitIcon: A('icon-unit.png')
  },
  pagination: {
    pageInfo: '共{total}条，每页显示{pageSize}条',
    prevPage: '上一页',
    nextPage: '下一页',
    loading: '加载中...'
  },
  footer: {
    copyright: '© 2026 杨凌农业科技成果转化综合信息服务平台 版权所有',
    support: '技术支持：浪潮软件'
  }
}

const enUS = {
  siteInfo: {
    title: 'Yangling Agricultural Science & Technology Achievement Transformation Platform',
    logo: L('shouye/web-logo.png'),
    loginText: 'Login',
    registerText: 'Register',
    logoutText: 'Logout'
  },
  breadcrumb: {
    home: 'Home',
    parent: 'Innovation Service',
    parentPath: '/innovation-service',
    current: 'Instruments'
  },
  header: {
    titleMain1: 'Research',
    titleMain2: 'Instruments',
    titleSub: 'Instrument interconnection for innovation win-win',
    searchPlaceholder: 'Please enter keywords to search',
    searchButton: 'Search',
    bannerBg: A('banner-bg.png')
  },
  modules: [
    { id: 'home', name: 'Home', enName: 'HOME', icon: Nav('nav-home.png'), link: '/index' },
    { id: 'innovation', name: 'Yangling', enName: 'YANGLING', icon: Nav('nav-innovation.png'), link: '/innovation' },
    { id: 'supply', name: 'Supply & Demand', enName: 'SUPPLY & DEMAND', icon: Nav('nav-supply.png'), link: '/supply-demand' },
    { id: 'evaluation', name: 'Evaluation', enName: 'EVALUATE', icon: Nav('nav-evaluation.png'), link: '/value-assessment' },
    { id: 'manager', name: 'Managers', enName: 'MANAGER', icon: Nav('nav-manager.png'), link: '/manager' },
    { id: 'transaction', name: 'Transaction', enName: 'TRANSACTION', icon: Nav('nav-transaction.png'), link: '/transaction' },
    { id: 'service', name: 'Service', enName: 'SERVICE', icon: Nav('nav-service.png'), link: '/innovation-service' },
    { id: 'talent', name: 'Talent', enName: 'TALENT', icon: Nav('nav-talent.png'), link: '/talent' },
    { id: 'enterprise', name: 'Enterprises', enName: 'ENTERPRISE', icon: Nav('nav-enterprise.png'), link: '/agriculture-enterprise' }
  ],
  icons: zhCN.icons,
  typeFilter: {
    label: 'Type',
    allLabel: 'All'
  },
  card: {
    priceLabel: 'Service Price:',
    priceDefault: 'Negotiable',
    emptyTip: '待接口提供',
    coverFallback: L('default.png'),
    unitIcon: A('icon-unit.png')
  },
  pagination: {
    pageInfo: '{total} items, {pageSize} per page',
    prevPage: 'Prev',
    nextPage: 'Next',
    loading: 'Loading...'
  },
  footer: {
    copyright: '© 2026 Yangling Agricultural Science & Technology Marketplace. All rights reserved.',
    support: 'Technical support: Inspur Software'
  }
}

export function getInstrumentsNewConfig(locale = getCurrentLocale()) {
  let raw
  if (locale === 'en-US') raw = deepCloneConfig(enUS)
  else if (locale === 'ru-RU') raw = applyPortalZhToRu(deepCloneConfig(zhCN))
  else raw = deepCloneConfig(zhCN)
  return resolveLocaleImages(raw, locale)
}
